import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { handleApi } from "../../utils/authentication";
import { toast } from "react-toastify";
import AnalyticsDetailsModal from "../../components/superadmin/AnalyticsDetailsModal";
import LoadingSpinner from "../../components/client/LoadingSpinner";

const ClientInsights = () => {
  const { client_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState({
    total_programs: 0,
    total_campaigns: 0,
    total_integrations: 0,
    total_spent: 0,
    total_users: 0,
    total_impressions: 0,
  });

  const [selectedInterval, setSelectedInterval] = useState("All Time");
  const [modalShow, setModalShow] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalTitle, setModalTitle] = useState("");

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const method = "POST";
      const headers = {};
      const formData = new FormData();
      formData.append("client_id", client_id);
      if (selectedInterval) {
        formData.append("time_interval", selectedInterval);
      }

      const result = await handleApi(
        "client_insights",
        method,
        headers,
        formData,
      );
      if (result?.success) {
        setInsights(result?.data);
      } else {
        toast.error(
          result?.error?.error_message
            ? result?.error?.error_message
            : "Failed to fetch client insights",
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          },
        );
      }
    } catch (e) {
      toast.error(e.message ? e.message : "Failed to fetch client insights", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (client_id) {
      fetchInsights();
    }
  }, [client_id, selectedInterval]);

  const handleIntervalClick = (interval) => {
    setSelectedInterval(interval);
  };

  const summaryData = [
    {
      backgroundColor: "#FFB200",
      icon: "bi bi-person-lines-fill",
      title: "Total Users",
      value: insights?.total_users ?? 0,
      type: "users",
    },
    {
      backgroundColor: "#81c14b",
      icon: "bi bi-bar-chart-line",
      title: "Total Programs",
      value: insights?.total_programs ?? 0,
      type: "programs",
    },
    {
      backgroundColor: "#0b4f74",
      icon: "bi bi-graph-up",
      title: "Total Campaigns",
      value: insights?.total_campaigns ?? 0,
      type: "campaigns",
    },
    {
      backgroundColor: "#cd8200",
      icon: "bi bi-plug",
      title: "Total Integrations",
      value: insights?.total_integrations ?? 0,
      type: "integrations",
    },
    // {
    //   backgroundColor: '#7f56d9',
    //   icon: 'bi bi-currency-dollar',
    //   title: 'Total Spent',
    //   value: `$${insights?.total_spent ?? 0}`,
    //   type: 'spending',
    // },
    // {
    //   backgroundColor: '#eb5454',
    //   icon: 'bi bi-bar-chart',
    //   title: 'Total Impressions',
    //   value: insights?.total_impressions?.toLocaleString() ?? 0,
    //   type: 'impressions',
    // },
  ];

  const handleCardClick = (cardData) => {
    setModalType(cardData.type);
    setModalTitle(cardData.title);
    setModalShow(true);
  };

  return (
    <div className="container-fluid d-flex flex-column gap_lg p-0 position-relative">
      {loading && <LoadingSpinner />}
      <div className="custom-card p-3">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Client Insights</h5>
        </div>
      </div>
      <div className="d-flex flex-wrap gap_md align-items-center">
        {[
          { label: "This Week", value: "week" },
          { label: "This Month", value: "month" },
          { label: "3 Months", value: "3_months" },
          { label: "6 Months", value: "6_months" },
          { label: "This Year", value: "year" },
          { label: "All Time", value: "All Time" },
        ].map((interval, index) => (
          <IntervalButton
            key={index}
            label={interval.label}
            onClick={() => handleIntervalClick(interval.value)}
            isSelected={selectedInterval === interval.value}
          />
        ))}
      </div>
      <div className="row g-4">
        {summaryData.map((data, index) => (
          <SummaryCard
            key={index}
            backgroundColor={data.backgroundColor}
            icon={data.icon}
            title={data.title}
            value={data.value}
            onClick={() => handleCardClick(data)}
          />
        ))}
      </div>

      <AnalyticsDetailsModal
        show={modalShow}
        setShow={setModalShow}
        title={modalTitle}
        type={modalType}
        interval={selectedInterval === "All Time" ? null : selectedInterval}
        fromDate={null}
        toDate={null}
        clientId={client_id}
      />
    </div>
  );
};

export default ClientInsights;

const IntervalButton = ({ label, onClick, isSelected }) => (
  <button
    className={`btn btn-md py-2 px-3 border rounded-pill ${isSelected ? "btn-primary" : ""}`}
    onClick={onClick}
  >
    <span className="small">{label}</span>
  </button>
);

const SummaryCard = ({ backgroundColor, icon, title, value, onClick }) => (
  <div className="col-xxl-3 col-lg-4 col-md-6 col-sm-12">
    <div
      className="summary-box text-white p-4 custom-card"
      style={{ backgroundColor, cursor: "pointer" }}
      onClick={onClick}
    >
      <div className="d-flex gap-3">
        <div
          className="border_radius_sm"
          style={{
            width: "60px",
            height: "60px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <i className={icon} style={{ fontSize: "40px", color: "white" }}></i>
        </div>
        <div>
          <h6>{title}</h6>
          <h5>{value}</h5>
        </div>
      </div>
    </div>
  </div>
);
